// src/RegionalPlansPage.js
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from './firebase';
import { slugify } from './components/RegionalPlansSection';
import {
  ArrowLeft,
  Globe,
  Wifi,
  Database,
  Clock,
  ChevronDown,
  ChevronUp,
  ShoppingCart,
  X,
} from 'lucide-react';

const formatGB = (mb) => {
  if (mb === -1) return 'Unlimited';
  if (!mb && mb !== 0) return '—';
  const gb = mb / 1000;
  if (gb >= 1) return `${gb % 1 === 0 ? gb.toFixed(0) : gb.toFixed(1)} GB`;
  return `${mb} MB`;
};

const RegionalPlansPage = ({ user, setShowAuthModal }) => {
  const { regionSlug } = useParams();

  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI state mirroring CountryDetails UX
  const [showAllPlans, setShowAllPlans] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [showPlanDetailsModal, setShowPlanDetailsModal] = useState(false);
  const [planDetailsModalData, setPlanDetailsModalData] = useState(null);

  // Countries list controls
  const [countriesOpen, setCountriesOpen] = useState(true);

  // Listener
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'regional_plans'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setAllPlans(rows);
        setLoading(false);
      },
      (e) => {
        console.error('regional_plans listener error', e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  // Plans for this region
  const { regionName, heroImage, plans, countriesCovered } = useMemo(() => {
    const filtered = allPlans.filter(
      (p) => slugify(p.plan_id || p.plan_name || '') === regionSlug
    );

    // sort by duration asc, Unlimited last within same duration, then price asc
    filtered.sort((a, b) => {
      if ((a.duration || 0) !== (b.duration || 0)) return (a.duration || 0) - (b.duration || 0);
      const aUnlimited = !!a.is_unlimited || a.data_amount === -1;
      const bUnlimited = !!b.is_unlimited || b.data_amount === -1;
      if (aUnlimited !== bUnlimited) return aUnlimited ? 1 : -1;
      return (a.price || 0) - (b.price || 0);
    });

    const rep = filtered[0];
    const name = rep?.plan_name || rep?.plan_id || 'Region';
    const img = rep?.image_url || '';
    // Prefer countries_covered from first record (usually same across durations)
    const countries = Array.isArray(rep?.countries_covered) ? rep.countries_covered : [];

    return { regionName: name, heroImage: img, plans: filtered, countriesCovered: countries };
  }, [allPlans, regionSlug]);

  // Auto-select first plan
  useEffect(() => {
    if (plans && plans.length > 0) {
      setSelectedPlan(plans[0]);
    } else {
      setSelectedPlan(null);
    }
  }, [plans]);

  const handlePlanDetails = (plan, e) => {
    e.stopPropagation();
    setPlanDetailsModalData(plan);
    setShowPlanDetailsModal(true);
  };

  const handlePurchase = (plan) => {
    if (!user) {
      setShowAuthModal?.(true);
      return;
    }
    window.location.href = `/checkout?plan=${encodeURIComponent(
      JSON.stringify({
        id: plan.bundle_name || plan.id,
        country: regionName,                  // show region name in checkout
        countryCode: plan.plan_id || regionName,
        countryFlag: plan.image_url || heroImage,
        dataAmount:
          plan.is_unlimited || plan.data_amount === -1
            ? 'Unlimited'
            : `${(plan.data_amount / 1000).toFixed(1)} GB`,
        duration: plan.duration,
        price: (plan.price ?? 0).toFixed(2),
        planData: plan,
        isRegional: true,
      })
    )}`;
  };

  const PlanDetailsModal = () => {
    if (!planDetailsModalData) return null;
    const isUnlimited =
      !!planDetailsModalData.is_unlimited || planDetailsModalData.data_amount === -1;

    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg max-w-lg w-full p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-bold text-gray-900">Plan Details</h3>
            <button
              onClick={() => setShowPlanDetailsModal(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4">
            <div
              className={`p-4 rounded-lg ${
                isUnlimited
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600'
                  : 'bg-gradient-to-r from-blue-500 to-blue-600'
              } text-white`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  {isUnlimited ? (
                    <Wifi className="w-5 h-5 mr-2" />
                  ) : (
                    <Database className="w-5 h-5 mr-2" />
                  )}
                  <span className="font-bold text-lg">
                    {isUnlimited ? 'Unlimited Data' : `${formatGB(planDetailsModalData.data_amount)}`}
                  </span>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold">
                    ${(planDetailsModalData.price ?? 0).toFixed(2)}
                  </div>
                  <div className="text-xs opacity-90">USD</div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg space-y-3">
              <div className="flex justify-between">
                <span className="text-gray-600">Region:</span>
                <span className="font-semibold text-gray-900">{regionName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Duration:</span>
                <span className="font-semibold text-gray-900">
                  {planDetailsModalData.duration}{' '}
                  {planDetailsModalData.duration === 1 ? 'day' : 'days'}
                </span>
              </div>
              {Array.isArray(planDetailsModalData.speed) &&
                planDetailsModalData.speed.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Network:</span>
                    <span className="font-semibold text-gray-900">
                      {planDetailsModalData.speed.join(', ')}
                    </span>
                  </div>
                )}
              <div className="flex justify-between">
                <span className="text-gray-600">Auto-start:</span>
                <span
                  className={`font-semibold ${
                    planDetailsModalData.autostart ? 'text-green-600' : 'text-red-600'
                  }`}
                >
                  {planDetailsModalData.autostart ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              {Array.isArray(planDetailsModalData.groups) &&
                planDetailsModalData.groups.length > 0 && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Plan Type:</span>
                    <span className="font-semibold text-gray-900 text-xs">
                      {planDetailsModalData.groups.join(', ')}
                    </span>
                  </div>
                )}
            </div>

            {planDetailsModalData.description && (
              <div className="bg-blue-50 p-4 rounded-lg">
                <h4 className="font-semibold text-blue-900 mb-2">Description</h4>
                <p className="text-sm text-blue-800">{planDetailsModalData.description}</p>
              </div>
            )}

            <button
              onClick={() => {
                setShowPlanDetailsModal(false);
                handlePurchase(planDetailsModalData);
              }}
              className="w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-3 px-4 rounded-lg font-semibold transition-all duration-300 flex items-center justify-center"
            >
              <ShoppingCart className="w-4 h-4 mr-2" />
              {user ? 'Purchase This Plan' : 'Sign In to Purchase'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading regional plans…</p>
        </div>
      </div>
    );
  }

  if (!plans || plans.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center space-y-6 max-w-md mx-auto px-6">
          <Globe className="w-16 h-16 text-gray-300 mx-auto" />
          <h2 className="text-2xl font-bold text-gray-900 capitalize">
            {regionSlug.replace(/-/g, ' ')}
          </h2>
          <p className="text-gray-600">No plans found for this region.</p>
          <Link
            to="/browse"
            className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Browse All
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back */}
        <div className="mb-4 md:mb-6">
          <Link to="/browse" className="flex items-center text-[#7B3EF0] hover:underline">
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back
          </Link>
        </div>

        {/* Main layout (match CountryDetails look) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-8 mb-4 md:mb-8">
          {/* Left — hero and countries list */}
          <div className="relative">
            <h1 className="text-xl md:text-2xl font-bold mb-3 md:mb-4">{regionName}</h1>
            <div className="aspect-[4/3] rounded-xl md:rounded-2xl overflow-hidden shadow-lg md:shadow-2xl">
              {heroImage ? (
                <img src={heroImage} alt={regionName} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 flex items-center justify-center">
                  <Globe className="w-16 md:w-24 h-16 md:h-24 text-white opacity-80" />
                </div>
              )}
            </div>

            {/* Region description */}
            <div className="mt-4 md:mt-6">
              <p className="text-sm md:text-base text-gray-700 leading-relaxed">
                Explore {regionName} with flexible multi-country eSIM plans. Pick a duration and
                data option that fits your trip and connect across supported destinations.
              </p>
            </div>

            {/* Supported countries (2 columns, no scroller) */}
            <div className="mt-4 md:mt-6 border border-gray-200 rounded-xl bg-white">
              <button
                className="w-full px-4 py-3 flex items-center justify-between text-left"
                onClick={() => setCountriesOpen((v) => !v)}
              >
                <span className="font-semibold text-gray-900">
                  Supported countries {countriesCovered?.length ? `(${countriesCovered.length})` : ''}
                </span>
                {countriesOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>

              {countriesOpen && (
                <div className="px-4 pb-4 grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {countriesCovered && countriesCovered.length > 0 ? (
                    countriesCovered.map((c, i) => (
                      <div
                        key={`${c.iso || i}`}
                        className="flex items-center justify-between text-sm bg-gray-50 rounded-md px-3 py-2"
                      >
                        <span className="text-gray-800">{c.name || 'Unknown'}</span>
                        <span className="text-xs text-gray-500 ml-3">{c.iso}</span>
                      </div>
                    ))
                  ) : (
                    <div className="text-sm text-gray-500">No countries listed.</div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right — plans list */}
          <div className="space-y-3 md:space-y-4">
            {(() => {
              if (!plans || plans.length === 0) {
                return (
                  <div className="text-center py-6 md:py-8">
                    <Globe className="w-10 md:w-12 h-10 md:h-12 text-gray-300 mx-auto mb-3 md:mb-4" />
                    <p className="text-sm md:text-base text-gray-500">
                      No plans available for this region.
                    </p>
                  </div>
                );
              }

              const plansToShow = showAllPlans ? plans : plans.slice(0, 6);

              return plansToShow.map((plan, idx) => {
                const isUnlimited = !!plan.is_unlimited || plan.data_amount === -1;
                const isSelected =
                  selectedPlan && selectedPlan.id === plan.id && selectedPlan.duration === plan.duration;

                return (
                  <div
                    key={`${plan.id}-${plan.duration}-${idx}`}
                    className={`flex items-center justify-between p-3 md:p-4 rounded-lg md:rounded-XL border transition-all duration-300 cursor-pointer ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 shadow-md'
                        : 'border-gray-200 hover:border-blue-300 bg-white hover:shadow-sm'
                    }`}
                    onClick={() => setSelectedPlan(plan)}
                  >
                    <div className="flex items-center space-x-2 md:space-x-4 flex-1 min-w-0">
                      <button
                        onClick={(e) => handlePlanDetails(plan, e)}
                        className="px-2 md:px-3 py-1 md:py-1.5 bg-blue-100 text-black rounded-2xl md:rounded-3xl text-xs font-medium hover:bg-blue-200 transition-colors whitespace-nowrap"
                      >
                        <span className="hidden sm:inline">Plan Details</span>
                        <span className="sm:hidden">Details</span>
                      </button>

                      <div className="flex items-center space-x-2 md:space-x-3 min-w-0">
                        <div className="flex items-center justify-center w-8 md:w-10 h-5 md:h-6 bg-gray-100 rounded">
                          <span className="text-xs font-bold text-black">5G</span>
                        </div>
                        <div className="min-w-0">
                          <div className="text-base md:text-lg font-bold text-gray-900 truncate">
                            {isUnlimited ? 'Unlimited' : formatGB(plan.data_amount)}
                          </div>
                          <div className="text-xs md:text-sm text-gray-600 truncate">
                            For {plan.duration} {plan.duration === 1 ? 'DAY' : 'DAYS'}
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 md:space-x-3 flex-shrink-0">
                      <div className="text-right">
                        <div className="text-lg md:text-xl font-bold text-orange-500">
                          <span className="text-xs md:text-sm text-black mr-1 md:mr-2">USD</span>{' '}
                          {(plan.price ?? 0).toFixed(2)}
                        </div>
                      </div>
                      <div
                        className={`w-5 md:w-6 h-5 md:h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300'
                        }`}
                      >
                        {isSelected && <div className="w-1.5 md:w-2 h-1.5 md:h-2 bg-white rounded-full" />}
                      </div>
                    </div>
                  </div>
                );
              });
            })()}

            {/* Show more/less */}
            {plans.length > 6 && (
              <div className="text-center">
                <button
                  onClick={() => setShowAllPlans((v) => !v)}
                  className="inline-flex items-center px-4 md:px-6 py-2 md:py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors text-sm md:text-base"
                >
                  {showAllPlans ? (
                    <>
                      Show Less <ChevronUp className="w-4 h-4 ml-2" />
                    </>
                  ) : (
                    <>
                      See More Plans<ChevronDown className="w-4 h-4 ml-2" />
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Bottom purchase bar */}
        {selectedPlan && (
          <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-3 md:p-4 shadow-lg z-50">
            <div className="max-w-7xl mx-auto">
              {/* Mobile */}
              <div className="flex flex-col sm:hidden space-y-3">
                <div className="flex items-center space-x-3">
                  <Globe className="w-6 h-6 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 text-sm truncate">
                      {regionName} eSIM (Regional)
                    </div>
                    <div className="text-xs text-gray-600 truncate">
                      {selectedPlan.is_unlimited || selectedPlan.data_amount === -1
                        ? 'Unlimited'
                        : formatGB(selectedPlan.data_amount)}{' '}
                      • {selectedPlan.duration} {selectedPlan.duration === 1 ? 'Day' : 'Days'}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-xs text-gray-600">Total</div>
                    <div className="text-lg font-bold text-orange-500">
                      USD {(selectedPlan.price ?? 0).toFixed(2)}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => handlePurchase(selectedPlan)}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl font-semibold transition-colors w-full"
                >
                  Buy Now
                </button>
              </div>

              {/* Desktop */}
              <div className="hidden sm:flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <Globe className="w-8 h-8 text-blue-600" />
                  <div>
                    <div className="font-semibold text-gray-900">{regionName} eSIM (Regional)</div>
                    <div className="text-sm text-gray-600">
                      {selectedPlan.is_unlimited || selectedPlan.data_amount === -1
                        ? 'Unlimited'
                        : formatGB(selectedPlan.data_amount)}{' '}
                      • {selectedPlan.duration} {selectedPlan.duration === 1 ? 'Day' : 'Days'}
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-6">
                  <div className="text-right">
                    <div className="text-sm text-gray-600">Total</div>
                    <div className="text-xl font-bold text-orange-500">
                      USD {(selectedPlan.price ?? 0).toFixed(2)}
                    </div>
                  </div>
                  <button
                    onClick={() => handlePurchase(selectedPlan)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-colors"
                  >
                    Buy Now
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Plan details modal */}
      {showPlanDetailsModal && <PlanDetailsModal />}
    </div>
  );
};

export default RegionalPlansPage;
